import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/privacy.scss";
import "../css/policy-pages.scss";

const sections = [
  ["controller", "Who is responsible"],
  ["collect", "Information we collect"],
  ["sources", "Where it comes from"],
  ["purposes", "Purposes and permissions"],
  ["sharing", "Who receives information"],
  ["international", "International processing"],
  ["retention", "How long it is kept"],
  ["rights", "Your rights"],
  ["marketing", "Marketing and profiling"],
  ["children", "Children"],
  ["security", "Security"],
  ["contact", "Contact and complaints"],
];

function Privacy() {
  const { brandName, contact } = siteConfig;

  return (
    <div className="privacy-page policy-page">
      <nav className="privacy-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <Link to="/policies">Policies</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Privacy policy</span>
      </nav>

      <header className="privacy-page__header">
        <p className="privacy-page__eyebrow">Your personal information</p>
        <h1>Privacy policy</h1>
        <p>
          This notice explains what {brandName} collects through this
          storefront, why it is used, who may receive it and the choices
          available to you.
        </p>
        <span>Last updated: 23 October 2026</span>
      </header>

      <main className="privacy-page__layout">
        <aside className="privacy-page__contents" aria-label="On this page">
          <h2>On this page</h2>
          <ol>
            {sections.map(([id, label]) => (
              <li key={id}><a href={`#${id}`}>{label}</a></li>
            ))}
          </ol>
          <p>For browser storage details, read our <Link to="/cookies">cookie policy</Link>.</p>
        </aside>

        <article className="privacy-page__article">
          <section id="controller">
            <p className="privacy-page__section-label">01</p>
            <h2>Who is responsible</h2>
            <p>
              {brandName} is responsible for the storefront processing
              described in this notice. The legal seller identified on an
              order document may also be responsible for processing it carries
              out. Privacy questions and rights requests can be sent to
              {" "}<a href={`mailto:${contact.email}`}>{contact.email}</a>.
            </p>
            <address>
              <strong>{brandName}</strong>
              <span>{contact.address}</span>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </address>
            <div className="privacy-page__highlight">
              <strong>Our position</strong>
              <p>We do not sell personal information. We do not currently use first-party advertising trackers on this storefront.</p>
            </div>
          </section>

          <section id="collect">
            <p className="privacy-page__section-label">02</p>
            <h2>Information we collect</h2>
            <div className="privacy-page__card-grid">
              <article>
                <h3>Information you provide</h3>
                <ul>
                  <li>Name, email, delivery and billing details</li>
                  <li>Order choices, quantities and product preferences</li>
                  <li>Support messages, complaint details and return evidence</li>
                  <li>Information needed for a privacy or rights-holder request</li>
                </ul>
              </article>
              <article>
                <h3>Information created through use</h3>
                <ul>
                  <li>Basket, wishlist and latest-order browser records</li>
                  <li>Order reference, amount, currency and payment status</li>
                  <li>Basic device, network, security and server records</li>
                  <li>Delivery, refund and customer-care history where applicable</li>
                </ul>
              </article>
            </div>
            <p>Full card numbers and card security codes should be entered only on the payment provider's hosted page. Do not send them to us by email or through a support message.</p>
            <p>We do not intentionally request special-category information such as health, biometric, political or religious information. Please do not provide it unless it is genuinely necessary for a request.</p>
          </section>

          <section id="sources">
            <p className="privacy-page__section-label">03</p>
            <h2>Where information comes from</h2>
            <p>Most information comes directly from you when you use checkout, email customer care or exercise a right. Additional information can come from the payment provider, banks, delivery partners, fraud-prevention services, your device and publicly available sources used to verify a legal claim.</p>
            <p>If someone orders for you or sends us your details, we may receive information from that person. They should have authority to provide it.</p>
          </section>

          <section id="purposes">
            <p className="privacy-page__section-label">04</p>
            <h2>Purposes and permissions</h2>
            <div className="policy-table-wrap">
              <table>
                <caption>Why personal information is used</caption>
                <thead><tr><th>Purpose</th><th>Typical information</th><th>Why it is used</th></tr></thead>
                <tbody>
                  <tr><th scope="row">Process and deliver orders</th><td>Contact, delivery, order and transaction information</td><td>To provide the transaction and services you request</td></tr>
                  <tr><th scope="row">Payments, refunds and fraud prevention</th><td>Order reference, amount, payment status, device and security signals</td><td>To administer payments, protect transactions and meet applicable obligations</td></tr>
                  <tr><th scope="row">Customer care, returns and complaints</th><td>Messages, order history, images and requested remedy</td><td>To respond to requests, resolve concerns and provide remedies</td></tr>
                  <tr><th scope="row">Store operation and improvement</th><td>Technical logs, errors, feature use and aggregated statistics</td><td>To operate a reliable, accessible and secure service</td></tr>
                  <tr><th scope="row">Legal, tax, safety and rights matters</th><td>Order, complaint, recall and request records</td><td>To meet applicable obligations and establish or defend claims</td></tr>
                  <tr><th scope="row">Optional activity</th><td>Information specifically described when you choose it</td><td>With consent or another permission explained at collection</td></tr>
                </tbody>
              </table>
            </div>
            <p>For Singapore-related processing, personal data is collected, used or disclosed with consent or another permission under the Personal Data Protection Act 2012 (PDPA), as applicable. If information required for checkout is not provided, we may be unable to process the order.</p>
          </section>

          <section id="sharing">
            <p className="privacy-page__section-label">05</p>
            <h2>Who receives information</h2>
            <p>Only information reasonably needed for the task may be shared with providers supporting payment, hosting, security, communications, delivery, returns, professional advice and legal compliance. A payment provider independently controls information entered on its hosted page under its own notice.</p>
            <p>Information may also be disclosed to a bank, regulator, court, law-enforcement body, rights holder or successor business where there is a valid legal basis. We do not give personal information to data brokers for sale.</p>
          </section>

          <section id="international">
            <p className="privacy-page__section-label">06</p>
            <h2>International processing</h2>
            <p>Online service, payment or delivery providers may process information outside Singapore or your country. For personal data transferred from Singapore, we take appropriate steps required by the PDPA to ensure a standard of protection comparable to the protection under the PDPA. Other transfer safeguards apply where required by the law governing the processing.</p>
            <p>Contact us for information about the safeguard relevant to a particular transfer.</p>
          </section>

          <section id="retention">
            <p className="privacy-page__section-label">07</p>
            <h2>How long information is kept</h2>
            <p>Records are kept only as long as reasonably necessary for the relevant purpose, including fulfilment, returns, safety, fraud prevention, tax, accounting, disputes and legal requirements. Order and invoice records are typically kept for the applicable statutory accounting period; support and security records are reviewed sooner unless needed for a continuing issue.</p>
            <p>Basket and wishlist data remain in your browser until you remove them or clear site data. The latest-order summary normally lasts only for the browser session. See the <Link to="/cookies">cookie policy</Link> for exact keys.</p>
          </section>

          <section id="rights">
            <p className="privacy-page__section-label">08</p>
            <h2>Your rights</h2>
            <p>Under Singapore's PDPA, you may request access to personal data about you and information about how it was used or disclosed, request correction of inaccurate or incomplete data, and withdraw consent subject to applicable conditions and exceptions. Other rights may apply under the law governing particular processing.</p>
            <p>Some rights have legal exceptions. For example, an order record may need to be retained for tax or legal claims even after a deletion request. We may ask for proportionate identity evidence and clarification before acting. We will not ask for full payment-card information.</p>
            <p>Send a request to <a href={`mailto:${contact.email}`}>{contact.email}</a> with “Privacy request” in the subject line. We will respond within the period required by applicable law.</p>
          </section>

          <section id="marketing">
            <p className="privacy-page__section-label">09</p>
            <h2>Marketing and automated decisions</h2>
            <p>This storefront does not currently provide a marketing-newsletter sign-up. Essential order, safety, return and support messages are not marketing and may still be sent where needed to provide the service.</p>
            <p>Security and payment providers may use automated signals to flag transactions. A significant decision will not be based solely on automated processing where the law prohibits it. Contact customer care to request a review of a declined order.</p>
          </section>

          <section id="children">
            <p className="privacy-page__section-label">10</p>
            <h2>Children</h2>
            <p>Checkout is intended for adults capable of entering a purchase contract. We do not knowingly seek children's contact or payment information, and the store is not designed for behavioural advertising to children. Parents and guardians can read the <Link to="/children-privacy">children's privacy policy</Link> or ask us to remove information submitted without appropriate involvement.</p>
          </section>

          <section id="security">
            <p className="privacy-page__section-label">11</p>
            <h2>Security</h2>
            <p>Reasonable administrative and technical measures are used to reduce unauthorised access, loss, alteration and disclosure. Payment credentials are entered on a hosted payment page. Access to operational information should be limited to people and providers who need it.</p>
            <p>No online service can guarantee absolute security. Report suspicious messages or a vulnerability under our <Link to="/security-policy">security and fraud policy</Link>.</p>
          </section>

          <section id="contact">
            <p className="privacy-page__section-label">12</p>
            <h2>Contact, complaints and changes</h2>
            <p>Send privacy questions or requests to <a href={`mailto:${contact.email}`}>{contact.email}</a> or write to {contact.address}. Concerns about Singapore-related processing may also be raised with Singapore's Personal Data Protection Commission; another data-protection authority may be relevant to processing in your location.</p>
            <p>We may update this notice when the storefront, providers or legal requirements change. The current version and date will remain on this page. Material changes will be highlighted where reasonably necessary.</p>
          </section>

          <footer className="privacy-page__article-footer">
            <span>Related information</span>
            <Link to="/cookies">Cookie policy</Link>
            <Link to="/security-policy">Security</Link>
            <Link to="/policies">All policies</Link>
          </footer>
        </article>
      </main>
    </div>
  );
}

export default Privacy;
